// اعتبار — everything the two surfaces must agree about.
//
// The service→label/colour vocabulary, the ledger-description parsing, the KPI
// arithmetic and the grouping. Both `Credits.web.jsx` and `Credits.mobile.jsx`
// import from here, so the phone and the workstation can never disagree about
// what a row says or what «این ماه» adds up to. (Profile.shared.js and
// Visit.shared.js exist for the same reason.)

import { C } from '../../constants/theme.js';
import {
  SparkIcon, ShieldIcon, MicIcon, PlusIcon, GiftIcon, ChatIcon,
} from './CreditIcons.jsx';

// ── Service identity ────────────────────────────────────────────────────────
//
// Every ledger row carries the WALLET'S service type, and the wallet knows only
// its own five (wallet.proto ServiceType). It does not know that a debit came
// from تشخیص افتراقی rather than from پرسش از دستیار: both are AI_LLM_SUMMARY on
// the wire, and the finer name lives in the row's `description` JSON, which
// `parseTransactionLabels` reads. So this map is the COARSE identity — the icon
// and the colour — and the row's title is the fine one.

export const SERVICE_META = (t) => ({
  AI_LLM_SUMMARY: { label: t('credits.service.assistant'), color: C.navy, bg: 'color-mix(in srgb, var(--p-navy) 10%, transparent)', icon: SparkIcon },
  AI_INTERACTION_CHECK: { label: t('credits.service.interaction'), color: C.amber, bg: C.amberLight, icon: ShieldIcon },
  AI_TRANSCRIPTION: { label: t('credits.service.transcription'), color: C.teal, bg: C.tealLight, icon: MicIcon },
  TOP_UP: { label: t('credits.service.topUp'), color: C.green, bg: C.greenLight, icon: PlusIcon },
  GIFT_CODE: { label: t('credits.service.gift'), color: C.green, bg: C.greenLight, icon: GiftIcon },
  SMS: { label: t('credits.service.sms'), color: C.teal, bg: C.tealLight, icon: ChatIcon },
});

export const DEFAULT_META = (t) => ({
  label: t('credits.service.ai'),
  color: C.navy,
  bg: 'color-mix(in srgb, var(--p-navy) 10%, transparent)',
  icon: SparkIcon,
});

// Rows written before the wallet stored the enum NAME carry the proto INTEGER
// as a string. They are still in the ledger and still have to render.
const SERVICE_TYPE_INT_MAP = { 1: 'SMS', 2: 'AI_TRANSCRIPTION', 3: 'AI_LLM_SUMMARY', 4: 'AI_INTERACTION_CHECK' };

/** Normalise whatever the wallet sent into a key of SERVICE_META. */
export function serviceKey(rawType) {
  const resolved = SERVICE_TYPE_INT_MAP[rawType] || rawType;
  return String(resolved || '').replace(/^SERVICE_TYPE_/, '');
}

export function getServiceMeta(rawType, t) {
  return SERVICE_META(t)[serviceKey(rawType)] || DEFAULT_META(t);
}

// ── Description parsing ─────────────────────────────────────────────────────

/** The row's description: a JSON blob the AI service wrote, or plain text. */
export function parseDescription(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/**
 * `{ title, sub, visitId }` for one row — title is the human action, sub is the
 * metering detail.
 *
 * TOKEN COUNTS AND THE MODEL NAME ARE DELIBERATELY NOT SHOWN. The AI service
 * records `input_tokens` / `output_tokens` / `model` on every deduction and
 * they stay in the ledger for cost analysis, but they are OUR unit economics,
 * not the dentist's: «۳۲٬۰۰۰ توکن · gemini-2.5-pro» tells them nothing they can
 * act on, and invites a question about which model ran instead of what the
 * session cost. The dentist sees what they spent, on what, and when.
 */
export function parseTransactionLabels(raw, t) {
  const parsed = parseDescription(raw);
  if (!parsed || typeof parsed === 'string') return { title: parsed || '', sub: '', visitId: null };

  const sub = [];
  if (parsed.duration_seconds) {
    sub.push(t('credits.minutesUsed', { count: Math.ceil(parsed.duration_seconds / 60) }));
  }
  return {
    title: parsed.action || '',
    sub: sub.join(' · '),
    // The visit id IS the appointment id in this platform (there is no
    // encounters table), which is what makes «۲۳ پارسه در این جلسه» computable.
    visitId: parsed.visit_id || parsed.appointment_id || parsed.patient_id || null,
  };
}

// ── Grouping ────────────────────────────────────────────────────────────────
//
// `formatDate` is passed IN rather than imported: it comes from
// `useTranslation()` and it is what pins every date to Asia/Tehran and to the
// active calendar. A `toLocaleDateString('fa-IR')` here would render a Persian
// date in the BROWSER'S timezone — which puts a 23:30 debit on the wrong day
// for a dentist who is travelling, and on the right one at home.

const dayKeyOf = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'unknown' : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

function dayLabel(iso, t, formatDate) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (days === 0) return t('credits.time.today');
  if (days === 1) return t('credits.time.yesterday');
  return formatDate(d.getTime(), 'full');
}

/** Group a chronological list into `[{ key, label, items }]` by calendar day. */
export function groupByDay(transactions, t, formatDate) {
  const groups = [];
  const index = {};
  transactions.forEach((item) => {
    const key = dayKeyOf(item.createdAt);
    if (index[key] == null) {
      index[key] = groups.length;
      groups.push({ key, label: dayLabel(item.createdAt, t, formatDate), items: [] });
    }
    groups[index[key]].items.push(item);
  });
  return groups;
}

/**
 * Group by service type into `[{ key, meta, total, items }]`, biggest first.
 *
 * `total` is NET and signed the way spending reads: a debit adds, a credit
 * subtracts. So a positive total is «خرج شد» and a negative one is «افزوده شد»,
 * which is what the group header renders with a − or a +.
 */
export function groupByType(transactions, t) {
  const map = {};
  transactions.forEach((item) => {
    const key = serviceKey(item.serviceType);
    if (!map[key]) map[key] = { key, meta: getServiceMeta(key, t), total: 0, items: [] };
    map[key].total += item.type === 'debit' ? item.amount : -item.amount;
    map[key].items.push(item);
  });
  return Object.values(map).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
}

// ── Date-range presets → ISO bounds ─────────────────────────────────────────
//
// The wallet filters `created_at >= start_date` / `<= end_date` and Postgres
// casts the ISO string. End bounds are pushed to 23:59:59 so the whole end-day
// is inside the window — without it «امروز» returns nothing until midnight.
//
// FIVE PRESETS AND NO ARBITRARY RANGE, unlike the doctor panel's credit screen.
// That screen uses the kit's `DateRangeFilter`, which hardcodes six Persian
// literals and `direction: 'rtl'` on its dropdown — fine in a Persian-only app,
// and a Persian popup inside an English UI here. This app ships `fa` AND `en`,
// so the window is chosen from chips whose labels come from the catalogue. A
// custom range belongs here the day the kit's picker takes its copy as props.

export const DATE_PRESETS = (t) => [
  { value: 'all', label: t('credits.range.all') },
  { value: 'today', label: t('credits.time.today') },
  { value: '7d', label: t('credits.range.days7') },
  { value: '30d', label: t('credits.range.days30') },
  { value: 'month', label: t('credits.range.thisMonth') },
];

const pad = (n) => String(n).padStart(2, '0');
const startISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
const endISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59:59`;

/** Resolve a preset to `{ startDate, endDate }`. 'all' means no bounds. */
export function rangeToISO(preset) {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { startDate: startISO(now), endDate: endISO(now) };
    case '7d': {
      const from = new Date(now);
      from.setDate(now.getDate() - 6);
      return { startDate: startISO(from), endDate: endISO(now) };
    }
    case '30d': {
      const from = new Date(now);
      from.setDate(now.getDate() - 29);
      return { startDate: startISO(from), endDate: endISO(now) };
    }
    case 'month':
      return { startDate: startISO(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: endISO(now) };
    default:
      return { startDate: '', endDate: '' };
  }
}

// ── KPIs ────────────────────────────────────────────────────────────────────

/**
 * The three numbers above the ledger: this month's spend, the personal
 * per-session average, and the service that took the biggest share.
 *
 * ALL OF IT IS COMPUTED OVER THE LOADED PAGE(S), not over the whole ledger.
 * That is a real limitation and it is deliberate: the wallet has no aggregate
 * RPC, and the alternative — pulling thousands of rows to add them up in a
 * browser — is worse than a figure that says "of what you can see". Replace
 * this with a server metric when the wallet grows one.
 */
export function computeStats(transactions) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

  let usedThisMonth = 0;
  let totalCredited = 0;
  let monthDebitTotal = 0;
  const byService = {};
  const visitSpend = {};

  transactions.forEach((item) => {
    const at = new Date(item.createdAt).getTime();
    if (item.type === 'debit') {
      const key = serviceKey(item.serviceType);
      if (at >= monthStart) {
        usedThisMonth += item.amount;
        monthDebitTotal += item.amount;
        byService[key] = (byService[key] || 0) + item.amount;
      }
      const { visitId } = parseTransactionLabels(item.description, () => '');
      if (visitId) visitSpend[visitId] = (visitSpend[visitId] || 0) + item.amount;
    } else {
      totalCredited += item.amount;
    }
  });

  const visitIds = Object.keys(visitSpend);
  const avgPerVisit = visitIds.length
    ? visitIds.reduce((sum, k) => sum + visitSpend[k], 0) / visitIds.length
    : null;

  let topService = null;
  let topAmount = 0;
  Object.entries(byService).forEach(([k, v]) => {
    if (v > topAmount) {
      topAmount = v;
      topService = k;
    }
  });
  const topPct = monthDebitTotal ? Math.round((topAmount / monthDebitTotal) * 100) : 0;

  return { usedThisMonth, totalCredited, avgPerVisit, topService, topPct, byService, monthDebitTotal };
}

/** Per-service spend rows (debits only, over the loaded page), biggest first. */
export function serviceBreakdown(transactions, t) {
  const map = {};
  transactions.forEach((item) => {
    if (item.type !== 'debit') return;
    const key = serviceKey(item.serviceType);
    if (!map[key]) map[key] = { amount: 0, count: 0, meta: getServiceMeta(key, t) };
    map[key].amount += item.amount;
    map[key].count += 1;
  });
  return Object.entries(map)
    .map(([key, v]) => ({ key, ...v }))
    .sort((a, b) => b.amount - a.amount);
}

// The three filters and the two grouping modes, shared so the phone's chips and
// the desktop's segmented controls offer the same set in the same order.
// `{ value, label }`, which is the kit's SegmentedControl and FilterChip shape.
export const TYPE_FILTERS = (t) => [
  { value: 'all', label: t('credits.filter.all') },
  { value: 'debit', label: t('credits.filter.spend') },
  { value: 'credit', label: t('credits.filter.topUp') },
];

export const GROUP_MODES = (t) => [
  { value: 'day', label: t('credits.group.day') },
  { value: 'type', label: t('credits.group.type') },
];

export const PAGE_SIZE = 30;
